import { Address, isPartialAddressValid } from '@getpopsure/public-models';
import {
  AutocompleteAddress,
  BottomOrRegularModal,
  Button,
  InformationBox,
  toast,
} from '@popsure/dirty-swan';
import { useUpdatePolicyAddress } from 'features/policySingle/hooks/useUpdateAddress';
import { useCallback, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useSafeTranslation } from 'shared/i18n';

import { PolicySingleModalProps } from './PolicySingleModal.models';

export const UpdateAddressModal = ({
  onClose,
  policyData: { attributes, id },
}: PolicySingleModalProps) => {
  const { t } = useSafeTranslation();
  const [address, setAddress] = useState<Partial<Address>>(
    attributes?.address || {}
  );
  const [showError, setShowError] = useState(false);

  const { updateAddress, isLoading, error, isSuccess } =
    useUpdatePolicyAddress(t);

  const isSameAddress = useCallback(() => {
    return (
      address &&
      attributes.address &&
      address?.houseNumber === attributes.address?.houseNumber &&
      address?.street === attributes.address?.street &&
      address?.city === attributes.address?.city &&
      address?.postcode === attributes.address?.postcode &&
      address?.country === attributes.address?.country
    );
  }, [address, attributes.address]);

  const isValidAddress =
    address && isPartialAddressValid(address) && !isSameAddress();

  const handleOnSubmit = () => {
    if (!isValidAddress || isLoading) {
      return;
    }

    updateAddress({ policyId: id, address });
  };

  useEffect(() => {
    if (isSuccess) {
      toast(
        t(
          'myPolicies.policyDetails.updateAddressModal.successMessage',
          'Your address was successfully updated'
        ),
        {
          type: 'success',
        }
      );
      onClose();
    }
  }, [isSuccess, onClose, t]);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  return (
    <BottomOrRegularModal
      isOpen
      onClose={onClose}
      title={t(
        'myPolicies.policyDetails.updateAddressModal.title',
        'Edit your address'
      )}
      footer={
        <>
          <p className="p-p tc-grey-600">
            {t(
              'myPolicies.policyDetails.updateAddressModal.footer.information',
              'Changing your address may affect the price of your policy. If it does, we’ll contact you by email before making any adjustments.'
            )}

            <div className="mt16 ta-right">
              <Button
                className="wmn4"
                disabled={!isValidAddress || !!showError || isSuccess}
                loading={isLoading}
                onClick={handleOnSubmit}
              >
                {t(
                  'myPolicies.policyDetails.updateAddressModal.footer.button',
                  'Submit'
                )}
              </Button>
            </div>
          </p>
        </>
      }
    >
      <div className="px24 pb32 pt8">
        <AutocompleteAddress
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ''}
          onAddressChange={(newAddress) => {
            setAddress(newAddress);
            setShowError(false);
          }}
          address={address}
        />
        <AnimateHeight duration={300} height={showError && error ? 'auto' : 0}>
          <InformationBox className="mt16" variant="warning">
            {error}
          </InformationBox>
        </AnimateHeight>
      </div>
    </BottomOrRegularModal>
  );
};
